// src/components/resources/tools/generators/AdMockup/InstagramAd.tsx
'use client'

import {
  ChatBubbleOvalLeftIcon,
  HeartIcon,
  PaperAirplaneIcon,
  BookmarkIcon,
  EllipsisHorizontalIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { CheckBadgeIcon as CheckBadgeSolidIcon } from '@heroicons/react/24/solid'
import { clsx } from 'clsx'
import { Avatar } from './Avatar'
import { CreativeSlot } from './CreativeSlot'
import { formatCount, handleOf } from './ad-format'
import type { AdState } from './types'

export function InstagramAd({ state, creativeImage, avatarImage }: { state: AdState; creativeImage: string | null; avatarImage: string | null }): JSX.Element {
  const dark = state.theme === 'dark'
  const handle = handleOf(state.pageName)
  return (
    <div className={clsx('overflow-hidden rounded-xl border font-sans shadow-md', dark ? 'border-slate-700 bg-black text-slate-100' : 'border-slate-200 bg-white text-slate-900')}>
      {/* header */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        <Avatar name={state.pageName} image={avatarImage} size={32} ring />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <span className="truncate text-[13px] font-semibold">{handle}</span>
            {state.verified && <CheckBadgeSolidIcon className="h-3.5 w-3.5 flex-shrink-0 text-[#3897F0]" aria-label="Verified" />}
          </div>
          <div className={clsx('text-[11px]', dark ? 'text-slate-400' : 'text-slate-500')}>Sponsored</div>
        </div>
        <EllipsisHorizontalIcon className={clsx('h-5 w-5 flex-shrink-0', dark ? 'text-slate-300' : 'text-slate-600')} />
      </div>

      {/* creative (1:1 square) */}
      <CreativeSlot image={creativeImage} alt={`${state.pageName} ad creative`} ratio={1} />

      {/* CTA banner — real IG feed link/CTA ads render the headline alongside the
          action, so we surface the headline here (keeping the field functional on
          this placement); the link card's description/display URL stay Facebook-only. */}
      <div className={clsx('flex items-center justify-between gap-3 px-3 py-2.5', dark ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 text-slate-900')}>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{state.headline}</span>
        <span className="flex flex-shrink-0 items-center gap-0.5 text-sm font-semibold text-[#3897F0]">
          {state.cta}
          <ChevronRightIcon className="h-4 w-4" />
        </span>
      </div>

      {/* action row */}
      <div className="flex items-center gap-4 px-3 pt-2.5">
        <HeartIcon className="h-6 w-6" />
        <ChatBubbleOvalLeftIcon className="h-6 w-6 -scale-x-100" />
        <PaperAirplaneIcon className="h-6 w-6 -rotate-45" />
        <BookmarkIcon className="ml-auto h-6 w-6" />
      </div>

      {/* likes + caption */}
      <div className="px-3 py-2 text-[13px]">
        <div className="font-semibold">{formatCount(state.likes)} likes</div>
        <p className="mt-1 leading-snug">
          <span className="font-semibold">{handle}</span>{' '}
          <span className="whitespace-pre-wrap">{state.primaryText}</span>
        </p>
        <div className={clsx('mt-1 text-xs', dark ? 'text-slate-400' : 'text-slate-500')}>
          View all {formatCount(state.comments)} comments
        </div>
      </div>
    </div>
  )
}
